/**
 * What One's item configuration widget reads from the catalogue item's
 * commands' dry run (Add to draft quote…; One E spec 13 §4.3): the item with its
 * fields, how it is priced now, and the price with its quantity ladder, in the
 * shapes the item page reads them (the catalogue's own transformers), so the
 * widget maps them with the item page's mappers and draws the item page's
 * specification and ladder.
 */
import type { components } from "../data/api-schema.generated";
import {
  pricePreviewFromSchema,
  resolvedItemFromSchema,
} from "../data/api/catalogue";
import type {
  CatalogueItem,
  CatalogueItemPricePreview,
} from "../data/catalogue-types";

/** How the item is priced now: by its preview, not automatically (no route prices it), or not yet (every route that
 * could price it is still pricing: the dry run says which, as a check). */
export type ItemPricing = "priced" | "unavailable" | "pending";

export interface ItemConfigurationContext {
  item: CatalogueItem;
  pricing: ItemPricing;
  /** The configuration's price and ladder; null when no route prices it. */
  price: CatalogueItemPricePreview | null;
}

const PRICINGS: readonly ItemPricing[] = ["priced", "unavailable", "pending"];

/** The dry run's `widget_context` for `configuration`, or null when it has not said (or says something else). */
export function itemConfigurationContextOf(
  raw: Record<string, unknown> | null | undefined,
): ItemConfigurationContext | null {
  const item = raw?.item as
    components["schemas"]["ResolvedCatalogueItemSchema"] | undefined;
  const pricing = raw?.pricing as ItemPricing | undefined;
  if (!item?.item || !Array.isArray(item.fields)) return null;
  if (!pricing || !PRICINGS.includes(pricing)) return null;
  const price = raw?.price as
    components["schemas"]["PricePreviewResponseSchema"] | null | undefined;
  return {
    item: resolvedItemFromSchema(item),
    pricing,
    price: price ? pricePreviewFromSchema(price) : null,
  };
}
